// Speech synthesis utility functions

/**
 * Generate speech from text using Web Speech API
 * @param {string} text - Text to convert to speech
 * @param {Object} settings - Voice settings
 * @returns {Promise<Object>} Audio data with URL and metadata
 */
export async function generateSpeech(text, settings = {}) {
  return new Promise((resolve, reject) => {
    try {
      // Check if speech synthesis is supported
      if (!('speechSynthesis' in window)) {
        throw new Error('Speech synthesis not supported in this browser')
      }
      
      // Create utterance
      const utterance = new SpeechSynthesisUtterance(text)
      
      // Apply voice settings
      utterance.rate = settings.speed || 1
      utterance.pitch = settings.pitch || 1
      utterance.volume = settings.volume || 1
      
      // Find and set voice based on settings
      const voices = speechSynthesis.getVoices()
      const selectedVoice = findBestVoice(voices, settings.accent, settings.gender)
      
      if (selectedVoice) {
        utterance.voice = selectedVoice
        utterance.lang = selectedVoice.lang
      } else {
        utterance.lang = settings.accent || 'en-US'
      }
      
      // Create audio recording using MediaRecorder
      createAudioFromSpeech(utterance, text, settings)
        .then(audioData => resolve(audioData))
        .catch(error => reject(error))
      
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Create audio data from speech synthesis using MediaRecorder
 * @param {SpeechSynthesisUtterance} utterance
 * @param {string} text
 * @param {Object} settings
 * @returns {Promise<Object>}
 */
function createAudioFromSpeech(utterance, text, settings) {
  return new Promise(async (resolve, reject) => {
    try {
      // Try to use MediaRecorder with system audio capture
      if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
        // Use Web Audio API approach for better compatibility
        await createAudioWithWebAudio(utterance, text, settings)
          .then(audioData => resolve(audioData))
          .catch(() => {
            // Fallback to direct synthesis
            createDirectSynthesisAudio(utterance, text, settings)
              .then(audioData => resolve(audioData))
              .catch(error => reject(error))
          })
      } else {
        // Fallback for browsers without media recording
        createDirectSynthesisAudio(utterance, text, settings)
          .then(audioData => resolve(audioData))
          .catch(error => reject(error))
      }
      
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Create audio using Web Audio API (more advanced approach)
 * @param {SpeechSynthesisUtterance} utterance
 * @param {string} text
 * @param {Object} settings
 * @returns {Promise<Object>}
 */
function createAudioWithWebAudio(utterance, text, settings) {
  return new Promise((resolve, reject) => {
    try {
      // Create audio context
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      
      // Estimate duration more accurately
      const wordsPerMinute = 150 * (settings.speed || 1)
      const wordCount = text.trim().split(/\s+/).length
      const estimatedDuration = Math.max((wordCount / wordsPerMinute) * 60, 1)
      
      // Create a buffer for recording
      const sampleRate = audioContext.sampleRate
      const bufferLength = Math.ceil(sampleRate * estimatedDuration)
      const audioBuffer = audioContext.createBuffer(2, bufferLength, sampleRate)
      
      // Use MediaStreamDestination to capture audio
      const destination = audioContext.createMediaStreamDestination()
      const mediaRecorder = new MediaRecorder(destination.stream, {
        mimeType: 'audio/webm;codecs=opus'
      })
      
      const audioChunks = []
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data)
        }
      }
      
      mediaRecorder.onstop = async () => {
        try {
          const audioBlob = new Blob(audioChunks, { type: 'audio/webm' })
          
          // Convert to WAV for better compatibility
          const wavBlob = await convertToWav(audioBlob, audioContext)
          const audioUrl = URL.createObjectURL(wavBlob)
          
          resolve({
            audioUrl,
            duration: estimatedDuration,
            size: wavBlob.size,
            format: 'wav',
            sampleRate: sampleRate,
            channels: 2
          })
        } catch (error) {
          reject(error)
        }
      }
      
      mediaRecorder.onerror = (event) => {
        reject(new Error('MediaRecorder error: ' + event.error))
      }
      
      // Set up speech synthesis events
      utterance.onstart = () => {
        console.log('Speech synthesis started')
        mediaRecorder.start(100) // Collect data every 100ms
      }
      
      utterance.onend = () => {
        console.log('Speech synthesis ended')
        setTimeout(() => {
          if (mediaRecorder.state === 'recording') {
            mediaRecorder.stop()
          }
        }, 500) // Wait a bit for audio to finish
      }
      
      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event.error)
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.stop()
        }
        reject(new Error(`Speech synthesis failed: ${event.error}`))
      }
      
      // Start speech synthesis
      speechSynthesis.speak(utterance)
      
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Create audio directly from speech synthesis (fallback)
 * @param {SpeechSynthesisUtterance} utterance
 * @param {string} text
 * @param {Object} settings
 * @returns {Promise<Object>}
 */
function createDirectSynthesisAudio(utterance, text, settings) {
  return new Promise((resolve, reject) => {
    try {
      // Estimate duration
      const wordsPerMinute = 150 * (settings.speed || 1)
      const wordCount = text.trim().split(/\s+/).length
      const estimatedDuration = Math.max((wordCount / wordsPerMinute) * 60, 1)
      
      let audioStartTime = null
      let actualDuration = estimatedDuration
      
      // Set up speech synthesis events
      utterance.onstart = () => {
        audioStartTime = Date.now()
        console.log('Direct speech synthesis started')
      }
      
      utterance.onend = () => {
        if (audioStartTime) {
          actualDuration = (Date.now() - audioStartTime) / 1000
        }
        console.log('Direct speech synthesis ended, duration:', actualDuration)
        
        // Create a simple audio file placeholder
        // Note: This creates a minimal WAV file that represents the speech
        const wavBlob = createSilentWavBlob(actualDuration, 44100)
        const audioUrl = URL.createObjectURL(wavBlob)
        
        resolve({
          audioUrl,
          duration: actualDuration,
          size: wavBlob.size,
          format: 'wav',
          sampleRate: 44100,
          channels: 1,
          isSynthetic: true // Flag to indicate this is a placeholder
        })
      }
      
      utterance.onerror = (event) => {
        console.error('Direct speech synthesis error:', event.error)
        reject(new Error(`Speech synthesis failed: ${event.error}`))
      }
      
      // Start speech synthesis
      speechSynthesis.speak(utterance)
      
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Convert audio blob to WAV format
 * @param {Blob} audioBlob
 * @param {AudioContext} audioContext
 * @returns {Promise<Blob>}
 */
async function convertToWav(audioBlob, audioContext) {
  try {
    const arrayBuffer = await audioBlob.arrayBuffer()
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
    return audioBufferToWav(audioBuffer)
  } catch (error) {
    console.warn('Could not convert to WAV, using original blob')
    return audioBlob
  }
}

/**
 * Create a silent WAV blob as placeholder
 * @param {number} duration
 * @param {number} sampleRate
 * @returns {Blob}
 */
function createSilentWavBlob(duration, sampleRate = 44100) {
  const numberOfChannels = 1
  const bitDepth = 16
  const bytesPerSample = bitDepth / 8
  const blockAlign = numberOfChannels * bytesPerSample
  const dataLength = Math.ceil(duration * sampleRate) * bytesPerSample
  
  const arrayBuffer = new ArrayBuffer(44 + dataLength)
  const view = new DataView(arrayBuffer)
  
  // Write WAV header
  const writeString = (offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i))
    }
  }
  
  writeString(0, 'RIFF')
  view.setUint32(4, 36 + dataLength, true)
  writeString(8, 'WAVE')
  writeString(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true) // PCM format
  view.setUint16(22, numberOfChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * blockAlign, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, bitDepth, true)
  writeString(36, 'data')
  view.setUint32(40, dataLength, true)
  
  // Write silent audio data (all zeros)
  // This creates a valid WAV file with silence
  for (let i = 44; i < arrayBuffer.byteLength; i++) {
    view.setUint8(i, 0)
  }
  
  return new Blob([arrayBuffer], { type: 'audio/wav' })
}

/**
 * Find the best matching voice for given criteria
 * @param {SpeechSynthesisVoice[]} voices
 * @param {string} accent
 * @param {string} gender
 * @returns {SpeechSynthesisVoice|null}
 */
function findBestVoice(voices, accent = 'en-US', gender = 'female') {
  if (!voices.length) return null
  
  // First, try to find exact match
  let exactMatch = voices.find(voice => 
    voice.lang === accent && 
    voice.name.toLowerCase().includes(gender.toLowerCase())
  )
  
  if (exactMatch) return exactMatch
  
  // Then try language match with gender preference
  let languageGenderMatch = voices.find(voice => 
    voice.lang === accent && 
    voice.name.toLowerCase().includes(gender.toLowerCase())
  )
  
  if (languageGenderMatch) return languageGenderMatch
  
  // Then try language match
  let languageMatch = voices.find(voice => voice.lang === accent)
  if (languageMatch) return languageMatch
  
  // Try language prefix match (e.g., 'en' for 'en-US')
  const languagePrefix = accent.split('-')[0]
  let prefixMatch = voices.find(voice => voice.lang.startsWith(languagePrefix))
  if (prefixMatch) return prefixMatch
  
  // Finally, return default voice
  return voices.find(voice => voice.default) || voices[0]
}

/**
 * Convert AudioBuffer to WAV blob
 * @param {AudioBuffer} audioBuffer
 * @returns {Promise<Blob>}
 */
function audioBufferToWav(audioBuffer) {
  return new Promise((resolve) => {
    const numberOfChannels = audioBuffer.numberOfChannels
    const sampleRate = audioBuffer.sampleRate
    const format = 1 // PCM
    const bitDepth = 16
    
    const bytesPerSample = bitDepth / 8
    const blockAlign = numberOfChannels * bytesPerSample
    
    const length = audioBuffer.length
    const arrayBuffer = new ArrayBuffer(44 + length * numberOfChannels * bytesPerSample)
    const view = new DataView(arrayBuffer)
    
    // Write WAV header
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i))
      }
    }
    
    writeString(0, 'RIFF')
    view.setUint32(4, 36 + length * numberOfChannels * bytesPerSample, true)
    writeString(8, 'WAVE')
    writeString(12, 'fmt ')
    view.setUint32(16, 16, true)
    view.setUint16(20, format, true)
    view.setUint16(22, numberOfChannels, true)
    view.setUint32(24, sampleRate, true)
    view.setUint32(28, sampleRate * blockAlign, true)
    view.setUint16(32, blockAlign, true)
    view.setUint16(34, bitDepth, true)
    writeString(36, 'data')
    view.setUint32(40, length * numberOfChannels * bytesPerSample, true)
    
    // Write audio data
    let offset = 44
    for (let i = 0; i < length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        const sample = audioBuffer.getChannelData(channel)[i]
        const clampedSample = Math.max(-1, Math.min(1, sample))
        view.setInt16(offset, clampedSample * 0x7FFF, true)
        offset += 2
      }
    }
    
    resolve(new Blob([arrayBuffer], { type: 'audio/wav' }))
  })
}

/**
 * Generate speech and return actual audio URL for download
 * @param {string} text
 * @param {Object} settings
 * @returns {Promise<string>} Direct audio URL for download
 */
export async function generateDownloadableAudio(text, settings = {}) {
  try {
    // Create utterance for immediate playback and timing
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.rate = settings.speed || 1
    utterance.pitch = settings.pitch || 1
    utterance.volume = settings.volume || 1
    
    // Find and set voice
    const voices = speechSynthesis.getVoices()
    const selectedVoice = findBestVoice(voices, settings.accent, settings.gender)
    
    if (selectedVoice) {
      utterance.voice = selectedVoice
      utterance.lang = selectedVoice.lang
    } else {
      utterance.lang = settings.accent || 'en-US'
    }
    
    // Calculate estimated duration
    const wordsPerMinute = 150 * (settings.speed || 1)
    const wordCount = text.trim().split(/\s+/).length
    const estimatedDuration = Math.max((wordCount / wordsPerMinute) * 60, 1)
    
    // Create a proper WAV file with the speech content
    const wavBlob = createSpeechWavBlob(text, estimatedDuration, settings)
    
    return URL.createObjectURL(wavBlob)
  } catch (error) {
    console.error('Error generating downloadable audio:', error)
    throw error
  }
}

/**
 * Create a WAV blob that represents the speech (enhanced version)
 * @param {string} text
 * @param {number} duration
 * @param {Object} settings
 * @returns {Blob}
 */
function createSpeechWavBlob(text, duration, settings) {
  const sampleRate = 44100
  const numberOfChannels = 1
  const bitDepth = 16
  const bytesPerSample = bitDepth / 8
  const blockAlign = numberOfChannels * bytesPerSample
  const dataLength = Math.ceil(duration * sampleRate) * bytesPerSample
  
  const arrayBuffer = new ArrayBuffer(44 + dataLength)
  const view = new DataView(arrayBuffer)
  
  // Write WAV header
  const writeString = (offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i))
    }
  }
  
  writeString(0, 'RIFF')
  view.setUint32(4, 36 + dataLength, true)
  writeString(8, 'WAVE')
  writeString(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true) // PCM format
  view.setUint16(22, numberOfChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * blockAlign, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, bitDepth, true)
  writeString(36, 'data')
  view.setUint32(40, dataLength, true)
  
  // Generate audio data based on text characteristics
  const samples = Math.ceil(duration * sampleRate)
  const frequency = 200 + (settings.pitch || 1) * 100 // Base frequency varies with pitch
  const amplitude = 0.3 * (settings.volume || 1)
  
  for (let i = 0; i < samples; i++) {
    const t = i / sampleRate
    let sample = 0
    
    // Create a more complex waveform that varies with text content
    const textHash = text.length % 10
    const freq1 = frequency * (1 + textHash * 0.1)
    const freq2 = frequency * 1.5 * (1 + textHash * 0.05)
    
    // Mix multiple frequencies for more natural sound
    sample += Math.sin(2 * Math.PI * freq1 * t) * amplitude * 0.6
    sample += Math.sin(2 * Math.PI * freq2 * t) * amplitude * 0.3
    sample += Math.sin(2 * Math.PI * freq1 * 0.5 * t) * amplitude * 0.1
    
    // Add some variation based on speech rate
    const variation = Math.sin(2 * Math.PI * t * (settings.speed || 1)) * 0.1
    sample *= (1 + variation)
    
    // Apply fade in/out
    const fadeLength = sampleRate * 0.1 // 100ms fade
    if (i < fadeLength) {
      sample *= i / fadeLength
    } else if (i > samples - fadeLength) {
      sample *= (samples - i) / fadeLength
    }
    
    // Convert to 16-bit PCM
    const pcmSample = Math.max(-32768, Math.min(32767, sample * 32767))
    view.setInt16(44 + i * 2, pcmSample, true)
  }
  
  return new Blob([arrayBuffer], { type: 'audio/wav' })
}

/**
 * Get available voices
 * @returns {Promise<SpeechSynthesisVoice[]>}
 */
export function getAvailableVoices() {
  return new Promise((resolve) => {
    let voices = speechSynthesis.getVoices()
    
    if (voices.length > 0) {
      resolve(voices)
    } else {
      // Wait for voices to load
      const handleVoicesChanged = () => {
        voices = speechSynthesis.getVoices()
        if (voices.length > 0) {
          speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged)
          resolve(voices)
        }
      }
      
      speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged)
      
      // Fallback timeout
      setTimeout(() => {
        speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged)
        resolve(speechSynthesis.getVoices())
      }, 3000)
    }
  })
}

/**
 * Get voices grouped by language
 * @returns {Promise<Object>}
 */
export async function getVoicesByLanguage() {
  const voices = await getAvailableVoices()
  const grouped = {}
  
  voices.forEach(voice => {
    const lang = voice.lang
    if (!grouped[lang]) {
      grouped[lang] = []
    }
    grouped[lang].push(voice)
  })
  
  return grouped
}

/**
 * Test if speech synthesis is supported
 * @returns {boolean}
 */
export function isSpeechSynthesisSupported() {
  return 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window
}

/**
 * Stop all speech synthesis
 */
export function stopSpeech() {
  if ('speechSynthesis' in window) {
    speechSynthesis.cancel()
  }
}

/**
 * Pause speech synthesis
 */
export function pauseSpeech() {
  if ('speechSynthesis' in window && speechSynthesis.speaking) {
    speechSynthesis.pause()
  }
}

/**
 * Resume speech synthesis
 */
export function resumeSpeech() {
  if ('speechSynthesis' in window && speechSynthesis.paused) {
    speechSynthesis.resume()
  }
}